import type { Site, WorkBlock } from '../../types/database';
import { coerceFiniteNumber } from '../../lib/number';
import type { OpsStructuredTask, OpsWorkBlock } from './types';
import { generateMonthlyOpsJobs, generateMonthlyOpsVisits } from './generator';
import type { GenerateMonthlyOpsJobsInput, OpsGeneratedJob, OpsGeneratedVisit, OpsSite } from './types';

export interface LiveRuleInput {
  year: number;
  month: number;
  sites: readonly Site[];
  workBlocks: readonly WorkBlock[];
  siteIds?: readonly string[];
  generated_at?: string | Date;
}

function normalizeWeekInMonth(value: WorkBlock['week_in_month']): OpsWorkBlock['week_in_month'] {
  if (value == null) {
    return null;
  }

  if (value === 'LAST') {
    return value;
  }

  const numeric = Number(value);
  return numeric === 1 || numeric === 2 || numeric === 3 || numeric === 4 ? numeric : null;
}

export function normalizeStructuredTasks(structured: readonly OpsStructuredTask[] | null | undefined): OpsStructuredTask[] {
  if (!structured || structured.length === 0) {
    return [];
  }

  return structured
    .map((task, index) => ({
      id: task.id?.trim() || `task-${index}`,
      label: task.label.trim(),
      points: coerceFiniteNumber(task.points),
    }))
    .filter((task) => task.label.length > 0);
}

export function computeBlockPoints(structuredTasks: readonly OpsStructuredTask[]): number {
  return Math.round(
    structuredTasks.reduce((sum, task) => sum + coerceFiniteNumber(task.points), 0) * 100,
  ) / 100;
}

export function mapSiteRowToOpsSite(site: Site): OpsSite {
  return {
    id: site.id,
    name: site.name,
    contract_path: '',
    service_start_date: site.service_start_date,
    purpose: '',
    notes: [],
    active: site.active,
  };
}

export function mapWorkBlockRowToOpsWorkBlock(block: WorkBlock): OpsWorkBlock {
  const structuredTasks = normalizeStructuredTasks((block.structured_tasks ?? []) as OpsStructuredTask[]);

  return {
    id: block.id,
    site_id: block.site_id,
    source_code: block.source_code,
    model_type: block.model_type,
    area: block.area,
    label: block.label,
    cadence: block.cadence,
    active_months: block.active_months,
    weekdays: block.weekdays as OpsWorkBlock['weekdays'],
    parity: block.parity,
    week_in_month: normalizeWeekInMonth(block.week_in_month),
    exact_months: block.exact_months,
    first_visit_of_month: Boolean((block as Partial<WorkBlock>).first_visit_of_month),
    required_photos: block.required_photos,
    default_user_id: block.default_user_id,
    one_off_date: (block as Partial<WorkBlock>).one_off_date ?? null,
    sort_order: block.sort_order,
    active: block.active,
    points: coerceFiniteNumber(block.points, computeBlockPoints(structuredTasks)),
    structured_tasks: structuredTasks,
  };
}

export function buildGenerateInputFromLiveRows(input: LiveRuleInput): GenerateMonthlyOpsJobsInput {
  const allowedSiteIds = input.siteIds == null ? null : new Set(input.siteIds);

  const sites = input.sites
    .filter((site) => site.active)
    .filter((site) => allowedSiteIds == null || allowedSiteIds.has(site.id))
    .map(mapSiteRowToOpsSite);

  const siteIdSet = new Set(sites.map((site) => site.id));
  const blocks = input.workBlocks
    .filter((block) => siteIdSet.has(block.site_id))
    .map(mapWorkBlockRowToOpsWorkBlock);

  return {
    year: input.year,
    month: input.month,
    generated_at: input.generated_at,
    sites,
    blocks,
  };
}

export function generateMonthlyOpsJobsFromLiveRows(input: LiveRuleInput): OpsGeneratedJob[] {
  return generateMonthlyOpsJobs(buildGenerateInputFromLiveRows(input));
}

export function generateMonthlyOpsVisitsFromLiveRows(input: LiveRuleInput): OpsGeneratedVisit[] {
  return generateMonthlyOpsVisits(buildGenerateInputFromLiveRows(input));
}

export function splitGeneratedJobsByKind(jobs: readonly OpsGeneratedJob[]): Record<'recurring' | 'extra', OpsGeneratedJob[]> {
  const recurring: OpsGeneratedJob[] = [];
  const extra: OpsGeneratedJob[] = [];

  for (const job of jobs) {
    if (job.job_kind === 'extra') {
      extra.push(job);
    } else {
      recurring.push(job);
    }
  }

  return { recurring, extra };
}
